import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Searchable combobox. options: [{ value, label, sublabel }]. Searches label + sublabel + value.
// Single mode: value is a string, onChange(string). Multi mode (multiple): value is string[], onChange(string[]).
// Async mode: pass onSearch(query) => Promise<[{value,label,sublabel}]> for server-side lookup of large lists.
export function SearchableSelect({ options = [], value, onChange, placeholder = "Select…", allLabel, className, testId, width = "w-72", multiple = false, onSearch, allowPattern = false }) {
  const [open, setOpen] = useState(false);
  const async = typeof onSearch === "function";
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!async || !open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await onSearch(query); if (active) setRemote(r || []); }
      catch { if (active) setRemote([]); }
      finally { if (active) setLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [async, open, query, onSearch]);

  const opts = async ? remote : options;
  const isPattern = (v) => /[*?]/.test(v || "");
  const pattern = allowPattern && isPattern(query) ? query.trim().toUpperCase() : "";

  if (multiple) {
    const selectedValues = Array.isArray(value) ? value : [];
    const labelFor = (v) => {
      const o = options.find((x) => x.value === v) || remote.find((x) => x.value === v);
      return o ? o.label : v;
    };
    const patterns = selectedValues.filter((v) => isPattern(v) && !options.some((o) => o.value === v));
    const display = selectedValues.length === 0
      ? (allLabel || placeholder)
      : selectedValues.length === 1
        ? labelFor(selectedValues[0])
        : `${selectedValues.length} selected`;
    const toggle = (v) => {
      if (selectedValues.includes(v)) onChange(selectedValues.filter((x) => x !== v));
      else onChange([...selectedValues, v]);
    };
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testId}
            className={cn(width, "h-9 justify-between border-slate-300 bg-white text-sm font-normal", className)}>
            <span className={cn("truncate", selectedValues.length === 0 && "text-slate-400")}>{display}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command
            shouldFilter={!async}
            filter={(val, search) => {
              const o = options.find((x) => x.value === val);
              const hay = `${o?.value || ""} ${o?.label || ""} ${o?.sublabel || ""} ${val}`.toLowerCase();
              return hay.includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput placeholder={allowPattern ? "Code, name, or pattern: 2506* / *FUR*" : "Search by code or name…"} data-testid={testId ? `${testId}-search` : undefined}
              value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>{loading ? "Searching…" : allowPattern ? "No match — type 2506* or *FUR* for a wildcard" : "No match found."}</CommandEmpty>
              <CommandGroup>
                {allLabel && (
                  <CommandItem value="__all__" onSelect={() => onChange([])}>
                    <Check className={cn("mr-2 h-4 w-4", selectedValues.length === 0 ? "opacity-100" : "opacity-0")} />
                    {allLabel}
                  </CommandItem>
                )}
                {pattern && (
                  <CommandItem value={pattern} onSelect={() => { toggle(pattern); setQuery(""); }} data-testid={testId ? `${testId}-pattern` : undefined}>
                    <Check className={cn("mr-2 h-4 w-4", selectedValues.includes(pattern) ? "opacity-100" : "opacity-0")} />
                    <span className="flex flex-col">
                      <span className="text-sm text-slate-800 font-mono">{pattern}</span>
                      <span className="text-xs text-slate-400">{pattern.endsWith("*") && !pattern.startsWith("*") ? `all WBS starting with ${pattern.slice(0, -1)}` : `all WBS matching ${pattern}`}</span>
                    </span>
                  </CommandItem>
                )}
                {patterns.filter((p) => p !== pattern).map((p) => (
                  <CommandItem key={p} value={p} onSelect={() => toggle(p)} data-testid={testId ? `${testId}-pattern-${p}` : undefined}>
                    <Check className="mr-2 h-4 w-4 opacity-100" />
                    <span className="text-sm text-slate-800 font-mono">{p}</span>
                  </CommandItem>
                ))}
                {opts.map((o) => (
                  <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)}>
                    <Check className={cn("mr-2 h-4 w-4", selectedValues.includes(o.value) ? "opacity-100" : "opacity-0")} />
                    <span className="flex flex-col">
                      <span className="text-sm text-slate-800">{o.label}</span>
                      {o.sublabel && <span className="text-xs text-slate-400">{o.sublabel}</span>}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  const selected = options.find((o) => o.value === value);
  const display = value === "" && allLabel ? allLabel : selected ? (selected.sublabel ? `${selected.value} · ${selected.label}` : selected.label) : placeholder;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(width, "h-9 justify-between border-slate-300 bg-white text-sm font-normal", className)}
        >
          <span className={cn("truncate", !selected && value !== "" && "text-slate-400")}>{display}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command
          filter={(val, search) => {
            const o = options.find((x) => x.value === val);
            const hay = `${o?.value || ""} ${o?.label || ""} ${o?.sublabel || ""} ${val}`.toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search by code or name…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {allLabel && (
                <CommandItem value="__all__" onSelect={() => { onChange(""); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === "" ? "opacity-100" : "opacity-0")} />
                  {allLabel}
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => { onChange(o.value); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="flex flex-col">
                    <span className="text-sm text-slate-800">{o.label}</span>
                    {o.sublabel && <span className="text-xs text-slate-400">{o.sublabel}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
